import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, TextField } from '@mui/material';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { DocumentSection } from '../../core/types/data-models';

interface EditSectionDialogProps {
  /** The section being edited; null keeps the dialog closed. */
  section: DocumentSection | null;
  onClose: () => void;
}

/** Edits one section's Markdown content. Status is left untouched — this view does not deal in section status. */
export function EditSectionDialog({ section, onClose }: EditSectionDialogProps) {
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (section) {
      setContent(section.content ?? '');
      setError(null);
    }
  }, [section]);

  const save = useMutation({
    mutationFn: () => resources.documentSections.update(section!.id, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-sections', section!.documentId] });
      onClose();
    },
    onError: (e) => setError(extractErrorMessage(e)),
  });

  return (
    <FormDialog
      open={section !== null}
      title={section ? `Edit: ${section.title}` : 'Edit section'}
      onClose={() => {
        if (!save.isPending) onClose();
      }}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      loading={save.isPending}
      maxWidth="md"
    >
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label="Content (Markdown)"
        multiline
        minRows={12}
        maxRows={28}
        fullWidth
        autoFocus
        value={content}
        onChange={(e) => setContent(e.target.value)}
        sx={{ '& .MuiInputBase-root': { fontFamily: 'var(--font-mono)', fontSize: '0.875rem' } }}
      />
    </FormDialog>
  );
}
